/**
 * Course-to-steer assembler (slice D, the tidal triangle). We want to make good
 * a particular ground track from A to B, but the tide will push us. What heading
 * do we steer, and how long will it take?
 *
 * The triangle, as a student draws it:
 *   1. From the start A, draw the tidal vector (set and rate over the passage
 *      time) to a point T.
 *   2. From T, the water track runs at the boat's speed to meet the desired
 *      ground track at the destination B. Its bearing is the course to steer.
 *   3. The ground track is A to B.
 *
 * Four things must stay distinct, because confusing them is the classic error:
 * the course to steer through the water, the desired ground track over the
 * ground, the tidal vector, and the bearing to the destination. The objects
 * below keep them as separate, labelled vectors.
 *
 * The maths is the tested nav courseToSteer, which also flags the case where the
 * tide is too strong to make the track at all, so we surface that rather than
 * drawing a nonsense triangle.
 */
import { courseToSteer, rhumbDestination, type CtsResult } from '@/nav';
import type { PositionPoint, Vector, CtsConstruction, PlotObject, TimeStamp } from './objects';

export interface BuildCTSParams {
  start: [number, number];
  to: [number, number];
  /** Boat speed through the water, knots. */
  boatSpeed: number;
  /** Tidal set: the direction the stream flows TOWARDS, degrees true. */
  tidalSet: number;
  /** Tidal rate, knots. */
  tidalRate: number;
  leewayDeg?: number;
  time?: TimeStamp;
  newId: (prefix: string) => string;
}

export interface BuiltCTS {
  result: CtsResult;
  /** The objects to add to the graph, or null when the track is unachievable. */
  objects: PlotObject[] | null;
  cts?: CtsConstruction;
}

export function buildCTS(params: BuildCTSParams): BuiltCTS {
  const { start, to, boatSpeed, tidalSet, tidalRate, leewayDeg, time, newId } = params;
  const result = courseToSteer({ from: start, to, tidalSet, tidalRate, boatSpeed, leewayDeg });

  if (result.unachievable) {
    return { result, objects: null };
  }

  const hours = result.hours;

  const startPt: PositionPoint = {
    id: newId('cstart'),
    kind: 'position_point',
    pointType: 'assumed',
    position: start,
    time,
    label: 'Start',
    source: { method: 'manual' },
    parents: [],
    children: [],
    userPlaced: true,
  };

  const destPt: PositionPoint = {
    id: newId('cdest'),
    kind: 'position_point',
    pointType: 'waypoint',
    position: to,
    time,
    label: 'Destination',
    source: { method: 'manual' },
    parents: [],
    children: [],
    userPlaced: true,
  };

  // T = start + the tidal vector over the passage time.
  const tideTipPos = rhumbDestination(start, tidalSet, tidalRate * hours);
  const tideTip: PositionPoint = {
    id: newId('ctip'),
    kind: 'position_point',
    pointType: 'marker',
    position: tideTipPos,
    time,
    label: 'Tide',
    source: { method: 'manual' },
    parents: [startPt.id],
    children: [],
    userPlaced: false,
  };

  const tidalVector: Vector = {
    id: newId('ctide'),
    kind: 'tidal_vector',
    start: startPt.id,
    directionDeg: tidalSet,
    rateKnots: tidalRate,
    interval: { startIso: time?.iso ?? '', endIso: '', hours },
    label: 'Tide',
    source: { method: 'manual' },
    parents: [startPt.id],
    children: [],
    userPlaced: false,
  };

  // The water track runs from the tide tip at the boat's speed; its bearing is
  // the course to steer (before leeway).
  const waterTrack: Vector = {
    id: newId('cwt'),
    kind: 'water_track',
    start: tideTip.id,
    directionDeg: result.waterTrackDeg,
    rateKnots: boatSpeed,
    interval: { startIso: time?.iso ?? '', endIso: '', hours },
    label: `CTS ${Math.round(result.ctsDeg)}°`,
    source: { method: 'manual' },
    parents: [tideTip.id],
    children: [],
    userPlaced: false,
  };

  const groundTrack: Vector = {
    id: newId('cgt'),
    kind: 'ground_track',
    start: startPt.id,
    directionDeg: result.groundTrackDeg,
    distanceNm: result.distanceNm,
    interval: { startIso: time?.iso ?? '', endIso: '', hours },
    source: { method: 'manual' },
    parents: [startPt.id, destPt.id],
    children: [],
    userPlaced: false,
  };

  const cts: CtsConstruction = {
    id: newId('cts'),
    kind: 'cts',
    time,
    label: `CTS ${Math.round(result.ctsDeg)}°`,
    source: { method: 'manual' },
    parents: [startPt.id, destPt.id, tidalVector.id, waterTrack.id, groundTrack.id],
    children: [],
    userPlaced: false,
    result: {
      ctsDeg: result.ctsDeg,
      waterTrackDeg: result.waterTrackDeg,
      groundTrackDeg: result.groundTrackDeg,
      sogKnots: result.sogKnots,
      hours: result.hours,
      unachievable: result.unachievable,
    },
  };

  return {
    result,
    cts,
    objects: [startPt, destPt, tideTip, tidalVector, waterTrack, groundTrack, cts],
  };
}
